import { useEffect, useState } from 'react';
import { ApiError, call } from '../api';
import { useApp } from '../context';
import { tmdbRef, type PlexItem } from '../plex';
import { linkProps } from '../router';
import { Avatar } from './Avatar';

/**
 * Aanraden: een film of serie naar een of meer vrienden sturen, met een kort berichtje.
 * Wat je krijgt staat als rij "Aangeraden door vrienden" op Home.
 */

interface Friend { id: string; name: string; thumb: string }
let cache: { at: number; list: Friend[] } | null = null;

function useFriends(): Friend[] | null {
  const [list, setList] = useState<Friend[] | null>(cache && Date.now() - cache.at < 60_000 ? cache.list : null);
  useEffect(() => {
    if (cache && Date.now() - cache.at < 60_000) return;
    call<{ friends: Friend[] }>('/api/friends').then((r) => { cache = { at: Date.now(), list: r.friends }; setList(r.friends); }).catch(() => setList([]));
  }, []);
  return list;
}

/** Wat je aanraadt: een titel uit Plex, of een titel van TMDB (voor wie alleen addons/debrid gebruikt). */
export interface TmdbTitle { type: 'movie' | 'tv'; tmdbId: number; title: string; year: number | null; poster: string | null; certification: string | null }

export function RecommendButton({ item, tmdb }: { item?: PlexItem; tmdb?: TmdbTitle }) {
  const friends = useFriends();
  const [open, setOpen] = useState(false);
  if (!friends?.length) return null;
  if (item && item.type !== 'movie' && item.type !== 'show') return null;
  if (!item && !tmdb) return null;
  return (
    <>
      <button className="btn" onClick={() => setOpen(true)}>Aanraden</button>
      {open && <RecommendDialog item={item} tmdb={tmdb} friends={friends} onClose={() => setOpen(false)} />}
    </>
  );
}

function RecommendDialog({ item, tmdb, friends, onClose }: { item?: PlexItem; tmdb?: TmdbTitle; friends: Friend[]; onClose: () => void }) {
  const { plex } = useApp();
  const [to, setTo] = useState<string[]>(friends.length === 1 ? [friends[0].id] : []);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const ref = item ? tmdbRef(item) : tmdb ? { type: tmdb.type, tmdbId: tmdb.tmdbId } : null;
  const title = item?.title ?? tmdb?.title ?? '';
  const year = item?.year ?? tmdb?.year ?? null;

  const send = () => {
    setBusy(true);
    setError(null);
    call<{ sent: string[]; blocked: string[] }>('/api/friends/recommend', {
      method: 'POST',
      body: JSON.stringify({
        to, note, title, year,
        key: item?.ratingKey ?? null,
        type: item ? item.type : tmdb!.type === 'tv' ? 'show' : 'movie',
        thumb: item?.thumb ?? null,
        tmdbId: ref?.tmdbId ?? null,
        poster: tmdb?.poster ?? null,
        contentRating: item?.contentRating ?? tmdb?.certification ?? null,
      }),
    })
      .then((r) => {
        const parts = [r.sent.length ? `Aangeraden aan ${r.sent.join(', ')}.` : '', r.blocked.length ? `Niet naar ${r.blocked.join(', ')}: dat profiel heeft een leeftijdsgrens.` : ''];
        setDone(parts.filter(Boolean).join(' '));
      })
      .catch((e: unknown) => setError(e instanceof ApiError && e.message !== e.code ? e.message : 'Versturen lukte niet.'))
      .finally(() => setBusy(false));
  };

  const poster = item?.thumb ? plex.image(item.thumb, 200, 300) : tmdb?.poster ?? undefined;
  return (
    <div className="swipe-match" role="dialog" aria-modal="true" aria-label="Aanraden" onClick={onClose}>
      <div className="swipe-match-body rec-dialog" onClick={(e) => e.stopPropagation()} data-focus-group>
        <div className="rec-head">
          {poster && <img src={poster} alt="" />}
          <div>
            <p className="subtle">Aanraden</p>
            <h2>{title}{year ? ` (${year})` : ''}</h2>
          </div>
        </div>
        {done ? (
          <>
            <p>{done}</p>
            <div className="form-actions"><button className="btn btn-primary" data-autofocus onClick={onClose}>Klaar</button></div>
          </>
        ) : (
          <>
            <div className="rec-friends" role="group" aria-label="Aan wie">
              {friends.map((f) => (
                <button key={f.id} className="chip rec-friend" aria-pressed={to.includes(f.id)} onClick={() => setTo((t) => (t.includes(f.id) ? t.filter((x) => x !== f.id) : [...t, f.id]))}>
                  <Avatar src={f.thumb} name={f.name} className="rec-avatar" />
                  {f.name}
                </button>
              ))}
            </div>
            <textarea className="input rec-note" value={note} maxLength={200} rows={3} placeholder="Waarom moet je dit zien? (mag leeg)" onChange={(e) => setNote(e.target.value)} />
            {error && <p className="error" role="alert">{error}</p>}
            <div className="form-actions">
              <button className="btn" onClick={onClose}>Annuleren</button>
              <button className="btn btn-primary" data-autofocus disabled={!to.length || busy} onClick={send}>{busy ? 'Versturen…' : 'Versturen'}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

interface Rec { id: string; from: Friend; href: string; key: string | null; type: 'movie' | 'show'; title: string; year: number | null; poster: string | null; note: string; at: number; seen: boolean; canOpen: boolean }

const ago = (ms: number) => {
  const d = Math.floor((Date.now() - ms) / 86_400_000);
  return d <= 0 ? 'vandaag' : d === 1 ? 'gisteren' : `${d} dagen geleden`;
};

/** Rij op Home: wat vrienden je aanraadden. Alleen zichtbaar als er iets is. */
export function FriendRecs() {
  const [recs, setRecs] = useState<Rec[]>([]);

  useEffect(() => {
    call<{ recommendations: Rec[] }>('/api/friends/recommendations')
      .then((r) => {
        setRecs(r.recommendations);
        if (r.recommendations.some((x) => !x.seen)) window.setTimeout(() => void call('/api/friends/recommendations/seen', { method: 'POST' }).catch(() => undefined), 4000);
      })
      .catch(() => setRecs([]));
  }, []);

  if (!recs.length) return null;
  const remove = (id: string) => {
    setRecs((r) => r.filter((x) => x.id !== id));
    void call(`/api/friends/recommendations/${id}`, { method: 'DELETE' }).catch(() => undefined);
  };
  return (
    <section className="rec-row" aria-label="Aangeraden door vrienden">
      <h2 className="rec-title">Aangeraden door vrienden</h2>
      <div className="rec-scroll" data-focus-group>
        {recs.map((r) => (
          <article key={r.id} className={`rec-card${r.seen ? '' : ' is-new'}`}>
            {/* Kan dit profiel de titel niet openen (andere bibliotheek, kinderslot of geen Plex), dan zoeken we hem op */}
            <a className="rec-poster" {...linkProps(r.href)} aria-label={`${r.title}, aangeraden door ${r.from.name}`}>
              {r.poster ? <img src={r.poster} alt="" loading="lazy" /> : <span className="swipe-noart">{r.title}</span>}
              {!r.seen && <span className="rec-badge">Nieuw</span>}
            </a>
            <div className="rec-body">
              <span className="rec-from"><Avatar src={r.from.thumb} name={r.from.name} className="rec-avatar" /><b>{r.from.name}</b><span className="subtle">· {ago(r.at)}</span></span>
              <b className="rec-name">{r.title}{r.year ? ` (${r.year})` : ''}</b>
              {r.note && <p className="rec-note-text">“{r.note}”</p>}
              {!r.canOpen && r.key && <span className="subtle rec-missing">Staat niet in jouw bibliotheek</span>}
              <button className="icon-mini rec-close" aria-label="Weghalen" onClick={() => remove(r.id)}>✕</button>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

/** Aanraden vanaf een addon-titel (IMDb-id): eerst het TMDB-id opzoeken. */
export function AddonRecommendButton({ imdb, title, year, poster }: { imdb: string; title: string; year: number | null; poster: string | null }) {
  const friends = useFriends();
  const [ref, setRef] = useState<{ type: 'movie' | 'tv'; tmdbId: number } | null>(null);
  useEffect(() => {
    if (!friends?.length) return;
    call<{ type: 'movie' | 'tv'; tmdbId: number }>(`/api/meta/find/${encodeURIComponent(imdb)}`).then(setRef).catch(() => setRef(null));
  }, [imdb, friends?.length]);
  if (!ref) return null;
  return <RecommendButton tmdb={{ ...ref, title, year, poster, certification: null }} />;
}
